import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { tap } from 'rxjs';

import { useAlarmLogListQuery, useAlarmLogRemoveAllQuery } from '@/hook';
import {
  Close,
  Done,
  Notifications,
  VBasePopover,
  VFlex,
  VStack,
  VTab,
  VTabContent,
  VTabTrigger,
  VTabTriggerContainer,
  VTextButton,
  cn,
  useFlag,
  useSubscriptions,
} from '@/package';
import { VIconButton } from '@/package/kiska/src/component/UI/VIconButton';
import { useWebSocket } from '@/provider';
import { useNotificationStore } from '@/store';

import NotificationList from './NotificationList';

const Notification = () => {
  const { alarmMessage$ } = useWebSocket();
  const { notificationRawList, addRawNotification, setRawNotificationList } =
    useNotificationStore();
  const {
    data: alarmLogList,
    isLoading: isAlarmLogListLoading,
    refetch,
  } = useAlarmLogListQuery();
  const { value: visible, setFalse: close, setTrue: open } = useFlag(false);

  useSubscriptions(
    () => [alarmMessage$.pipe(tap(addRawNotification)).subscribe()],
    [alarmLogList, alarmMessage$, notificationRawList],
  );

  useEffect(() => {
    if (isAlarmLogListLoading) return;

    setRawNotificationList(alarmLogList ?? []);
  }, [alarmLogList, isAlarmLogListLoading]);

  useEffect(() => {
    if (visible) return;

    refetch();
  }, [visible]);

  return (
    <VBasePopover
      placement="bottom-right"
      content={() => <NotificationContent close={close} />}
      className="!rounded-md !shadow-none"
      trigger="click"
      visible={visible}
      onUpdateVisible={v => (v ? open() : close())}
    >
      <VIconButton size="lg" variant="roundSecondary" Icon={Notifications} />
    </VBasePopover>
  );
};

const NotificationEmpty = () => {
  const { t } = useTranslation();

  return (
    <VFlex className="px w-full py-10" justify="center" items="center">
      <VStack gap={0.25} items="center">
        <Notifications className="h-7 w-7 text-gray-300" />
        <div className="text-body-xl text-gray-300">
          {t('alarm.emptyAlarmList.perplication')}
        </div>
      </VStack>
    </VFlex>
  );
};

const NotificationContent = ({ close }: { close: () => void }) => {
  const { notificationRawList } = useNotificationStore();
  const notificationLength = useMemo(
    () => notificationRawList.length,
    [notificationRawList.length],
  );
  const { t } = useTranslation();
  const { mutateAsync: removeAllAlarmLog } = useAlarmLogRemoveAllQuery();

  return (
    <VTab defaultValue="0" size="small">
      <VStack gap={0.5} className="!w-[424px] py-2">
        <VFlex className="w-full px-4" items="center" justify="between">
          <div className="text-headline-sm font-semibold">Notifications</div>
          <Close
            className="h-6 w-6 text-gray-400"
            role="button"
            onClick={close}
          />
        </VFlex>

        <VFlex className="w-full px-4" items="center" justify="between">
          <VTabTriggerContainer>
            <VTabTrigger value="0">
              {t('alarm.alarmTabAllAlarm.siderology')}
            </VTabTrigger>
          </VTabTriggerContainer>

          <VFlex
            className={cn({
              'cursor-pointer text-gray-500': notificationLength,
              'text-gray-300': !notificationLength,
            })}
            items="center"
            gap={0.25}
            onClick={() => notificationLength && removeAllAlarmLog()}
          >
            <Done
              className={cn('size-4', {
                '!cursor-not-allowed': !notificationLength,
              })}
            />
            <VTextButton
              className={cn('text-body-lg font-semibold', {
                '!cursor-not-allowed': !notificationLength,
              })}
            >
              {t('alarm.removeAll.toteload')}
            </VTextButton>
          </VFlex>
        </VFlex>

        <VTabContent value="0">
          {notificationLength ? <NotificationList /> : <NotificationEmpty />}
        </VTabContent>
      </VStack>
    </VTab>
  );
};

export default Notification;
